import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Note, Plus, PencilSimple, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import CallLogDialog from "@/components/CallLogDialog";
import { useConfirm } from "@/components/ConfirmDialog";

const SORT_OPTIONS = [
  { value: "recent", label: "Recent first" },
  { value: "oldest", label: "Oldest first" },
];

function fmtDur(total) {
  if (!total || total <= 0) return "";
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (h > 0 && m > 0) return `${h}h ${m}m`;
  if (h > 0) return `${h}h`;
  return `${m}m`;
}

/**
 * Per-firm Notes panel — opens from the Firm 360 header "Notes" button.
 *
 * Mirrors the Call Logs dialog layout: a "Log a note" button at the top that
 * opens the unified CallLogDialog (firm pinned, type locked to note), and a
 * table below with Time / Logged by / Remark / Duration. Same row-level
 * edit/delete permissions: authors can edit/delete within 30 days; Super Admins
 * can always delete; admins reuse the same path so notes are symmetric with
 * call logs.
 */
export default function NotesDialog({ open, onOpenChange, firm, onCountChange }) {
  const { user: me } = useAuth();
  const confirm = useConfirm();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sort, setSort] = useState("recent");
  const [showLog, setShowLog] = useState(false);
  const [editing, setEditing] = useState(null);  // a note row currently being edited via CallLogDialog

  const isSuperAdmin = me?.role === "super_admin";

  const fetchNotes = async () => {
    if (!firm?.subdomain) return;
    setLoading(true);
    try {
      const res = await api.get(`/firms/${firm.subdomain}/notes`);
      setRows(res.data.rows || []);
      onCountChange?.(res.data.total ?? (res.data.rows || []).length);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load notes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      setEditing(null);
      setShowLog(false);
      fetchNotes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, firm?.subdomain]);

  const sortedRows = useMemo(() => {
    const arr = [...rows];
    arr.sort((a, b) =>
      sort === "recent"
        ? (b.noted_at || b.created_at).localeCompare(a.noted_at || a.created_at)
        : (a.noted_at || a.created_at).localeCompare(b.noted_at || b.created_at)
    );
    return arr;
  }, [rows, sort]);

  const removeNote = async (note) => {
    if (!await confirm({
      title: "Delete note?",
      body: "This cannot be undone.",
      confirmLabel: "Delete note",
      destructive: true,
    })) return;
    try {
      await api.delete(`/notes/${note.note_id}`);
      toast.success("Note deleted");
      await fetchNotes();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  // CallLogDialog expects an `editing` row in unified-Activity shape; map our
  // notes-collection row into that shape so the dialog can pre-fill correctly
  // and the PATCH hits /notes/{id} via the existing entry_type branch.
  const editingForDialog = editing
    ? {
        entry_type: "note",
        entry_id: editing.note_id,
        note_id: editing.note_id,
        firm_subdomain: editing.firm_subdomain,
        firm_name: editing.firm_name,
        target_type: editing.target_type || "firm",
        occurred_at: editing.noted_at || editing.created_at,
        noted_at: editing.noted_at || editing.created_at,
        body: editing.body,
        remark: editing.body,
        duration_minutes: editing.duration_minutes,
        created_at: editing.created_at,
        updated_at: editing.updated_at,
      }
    : null;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col" data-testid="notes-dialog">
          <DialogHeader>
            <div className="flex items-start justify-between gap-4 pr-8">
              <div className="min-w-0">
                <DialogTitle className="font-display flex items-center gap-2">
                  <Note size={18} weight="fill" className="text-violet-600" />
                  Notes · {firm?.firm_name}
                </DialogTitle>
                <div className="text-xs text-gray-500 mt-0.5">
                  {loading ? "Loading…" : `${rows.length} note${rows.length === 1 ? "" : "s"} logged for this firm`}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Select value={sort} onValueChange={setSort}>
                  <SelectTrigger className="w-[140px] h-8 text-xs" data-testid="notes-sort">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SORT_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  size="sm"
                  onClick={() => setShowLog(true)}
                  className="bg-violet-600 hover:bg-violet-700 text-white"
                  data-testid="notes-add-btn"
                >
                  <Plus size={14} weight="bold" className="mr-1.5" />
                  Log a note
                </Button>
              </div>
            </div>
            <DialogDescription className="sr-only">
              Notes logged for {firm?.firm_name}. Click "Log a note" to add a new one. Authors can edit their own notes within 30 days.
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto -mr-2 pr-2 mt-2">
            {!loading && sortedRows.length === 0 && (
              <div className="py-12 text-center text-sm text-gray-400">
                No notes have been logged for this firm yet. Click "Log a note" above to add the first one.
              </div>
            )}
            {!loading && sortedRows.length > 0 && (
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-left sticky top-0 z-10">
                    <tr>
                      <th className="h-9 px-3 text-[10px] uppercase tracking-wider font-medium text-gray-500 border-b border-gray-200 w-44">Time</th>
                      <th className="h-9 px-3 text-[10px] uppercase tracking-wider font-medium text-gray-500 border-b border-gray-200 w-40">Logged by</th>
                      <th className="h-9 px-3 text-[10px] uppercase tracking-wider font-medium text-gray-500 border-b border-gray-200">Remark</th>
                      <th className="h-9 px-3 text-[10px] uppercase tracking-wider font-medium text-gray-500 border-b border-gray-200 w-20 text-right">Duration</th>
                      <th className="h-9 px-2 border-b border-gray-200 w-20"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedRows.map((n) => {
                      const isAuthor = n.created_by_email === me?.email;
                      const within30d = (new Date() - new Date(n.created_at)) < 30 * 24 * 3600 * 1000;
                      const canEdit = isAuthor && within30d;
                      const canDelete = canEdit || isSuperAdmin;
                      const firstName = (n.created_by_name || "").trim().split(/\s+/)[0] || n.created_by_name;
                      const time = n.noted_at || n.created_at;
                      return (
                        <tr key={n.note_id} className="border-b border-gray-100 last:border-b-0 align-top" data-testid={`note-row-${n.note_id}`}>
                          <td className="px-3 py-2 text-gray-700 whitespace-nowrap font-mono text-xs">
                            {new Date(time).toLocaleString("en-IN", {
                              day: "2-digit", month: "short", year: "numeric",
                              hour: "2-digit", minute: "2-digit",
                            })}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <span className="text-gray-900 text-xs cursor-help" title={`${n.created_by_name}\n${n.created_by_email || ""}`}>
                              {firstName}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-gray-700 text-xs leading-relaxed whitespace-pre-wrap">
                            {n.body}
                          </td>
                          <td className="px-3 py-2 text-right text-xs text-gray-700 font-mono whitespace-nowrap">
                            {n.duration_minutes ? fmtDur(n.duration_minutes) : <span className="text-gray-300">—</span>}
                          </td>
                          <td className="px-2 py-2 text-right whitespace-nowrap">
                            <div className="inline-flex items-center gap-1">
                              {canEdit && (
                                <Button size="sm" variant="ghost" onClick={() => setEditing(n)} className="h-6 w-6 p-0 text-gray-500 hover:text-violet-600" data-testid={`note-edit-${n.note_id}`}>
                                  <PencilSimple size={13} />
                                </Button>
                              )}
                              {canDelete && (
                                <Button size="sm" variant="ghost" onClick={() => removeNote(n)} className="h-6 w-6 p-0 text-red-500 hover:text-red-700 hover:bg-red-50" data-testid={`note-delete-${n.note_id}`}>
                                  <Trash size={13} />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Log a note dialog — firm pinned to this firm, type locked to note */}
      <CallLogDialog
        open={showLog || !!editing}
        editing={editingForDialog}
        lockedFirm={firm ? { subdomain: firm.subdomain, firm_name: firm.firm_name } : null}
        lockedType="note"
        onOpenChange={(v) => {
          if (!v) {
            setShowLog(false);
            setEditing(null);
          }
        }}
        onSaved={() => {
          setShowLog(false);
          setEditing(null);
          fetchNotes();
        }}
      />
    </>
  );
}
